import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import { brokerQuotes, type BrokerQuoteSettings } from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../../shared/fieldError';

/**
 * Configuracao da cotacao da corretora (decisoes abertas do dono, ADR 0054): a taxa no
 * assistente, o que a corretora le da linha do tempo e ate quando ela cancela a propria
 * cotacao. O padrao segue o OnPoint; a troca vale daqui em diante e fica na trilha.
 */
export function BrokerQuoteSettingsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const consulta = useQuery({
    queryKey: ['broker-quote-options'],
    queryFn: ({ signal }) => brokerQuotes.options(signal),
    enabled: open,
  });
  const [rascunho, setRascunho] = useState<BrokerQuoteSettings | null>(null);
  const atual = rascunho ?? consulta.data?.settings ?? null;

  const gravar = useMutation({
    mutationFn: (input: BrokerQuoteSettings) => brokerQuotes.updateSettings(input),
    onSuccess: () => {
      toast.success('Configuracao da cotacao da corretora gravada.');
      setRascunho(null);
      void queryClient.invalidateQueries({ queryKey: ['broker-quote-options'] });
      onClose();
    },
  });
  const apiError = gravar.error instanceof ApiError ? gravar.error : null;

  function fechar() {
    setRascunho(null);
    gravar.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={fechar}
      title="Cotacao da corretora"
      description="Vale para as cotacoes da corretora daqui em diante."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Cancelar
          </Button>
          <Button
            loading={gravar.isPending}
            disabled={!atual}
            onClick={() => atual && gravar.mutate(atual)}
          >
            Salvar configuracao
          </Button>
        </>
      }
    >
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a configuracao">
          {consulta.error.message}
        </Alert>
      ) : !atual ? (
        <SkeletonText lines={3} />
      ) : (
        <div className="flex flex-col gap-4">
          {gravar.error ? (
            <Alert tone="danger" title="Nao foi possivel salvar">
              {gravar.error.message}
            </Alert>
          ) : null}
          <Select
            label="Taxa na cotacao da corretora"
            value={atual.broker_rate_mode}
            options={[
              { value: 'sugerida', label: 'Sugerida pela faixa vigente, so leitura' },
              { value: 'faixa', label: 'Digitada pela corretora dentro da faixa' },
            ]}
            error={fieldErrorOf(apiError, 'broker_rate_mode')}
            onChange={(event) =>
              setRascunho({
                ...atual,
                broker_rate_mode: event.target
                  .value as BrokerQuoteSettings['broker_rate_mode'],
              })
            }
          />
          <Select
            label="Linha do tempo para a corretora"
            value={atual.broker_timeline_detail}
            options={[
              { value: 'resumo', label: 'Resumo, sem o detalhe interno' },
              { value: 'completo', label: 'Completa, com o detalhe dos eventos' },
            ]}
            error={fieldErrorOf(apiError, 'broker_timeline_detail')}
            onChange={(event) =>
              setRascunho({
                ...atual,
                broker_timeline_detail: event.target
                  .value as BrokerQuoteSettings['broker_timeline_detail'],
              })
            }
          />
          <Select
            label="Cancelamento pela corretora"
            value={atual.broker_withdraw_scope}
            options={[
              { value: 'cotacao', label: 'So antes do envio para analise' },
              { value: 'ate_emissao', label: 'Ate a etapa anterior a emissao' },
            ]}
            error={fieldErrorOf(apiError, 'broker_withdraw_scope')}
            onChange={(event) =>
              setRascunho({
                ...atual,
                broker_withdraw_scope: event.target
                  .value as BrokerQuoteSettings['broker_withdraw_scope'],
              })
            }
          />
          {atual.updated_by_label ? (
            <p className="text-2xs text-ink-500">
              Alterada por {atual.updated_by_label}
              {atual.updated_at ? ` em ${formatDateTime(atual.updated_at)}` : ''}.
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
